
import { useEffect, useRef } from 'react';

interface Pago {
  id: string;
  usuario_id: string;
  monto: number;
  metodo_pago: string;
  concepto: string;
  fecha_pago: string;
  estado: string;
  sucursal_id: string;
  created_at: string;
  banco?: string;
  numero_comprobante?: string;
  notas?: string;
  usuario?: {
    nombre: string;
    email: string;
    telefono: string;
    tipo_membresia: string;
  };
}

interface DetallePagoModalProps {
  pago: Pago;
  onClose: () => void;
}

export default function DetallePagoModal({ pago, onClose }: DetallePagoModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const formatearMonto = (monto: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(monto);
  };

  const formatearFecha = (fecha: string) => {
    // Postgres `date` arrives as "YYYY-MM-DD". JS parses that as UTC and can show the previous day in CL timezone.
    const d = /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? new Date(`${fecha}T00:00:00`) : new Date(fecha);
    return d.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatearFechaHora = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-CL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      case 'rechazado':
        return 'bg-red-100 text-red-700 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const obtenerIconoEstado = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'ri-checkbox-circle-fill text-green-500';
      case 'pendiente':
        return 'ri-time-fill text-yellow-500';
      case 'rechazado':
        return 'ri-close-circle-fill text-red-500';
      default:
        return 'ri-question-fill text-gray-500';
    }
  };

  const obtenerIconoMetodo = (metodo: string) => {
    switch (metodo) {
      case 'efectivo':
        return 'ri-money-dollar-circle-line';
      case 'tarjeta':
        return 'ri-bank-card-line';
      case 'transferencia':
        return 'ri-exchange-line';
      case 'cheque':
        return 'ri-file-list-3-line';
      case 'deposito':
        return 'ri-bank-line';
      default:
        return 'ri-wallet-line';
    }
  };

  const obtenerNombreMetodo = (metodo: string) => {
    switch (metodo) {
      case 'efectivo': return 'Efectivo';
      case 'tarjeta': return 'Tarjeta';
      case 'transferencia': return 'Transferencia Bancaria';
      case 'cheque': return 'Cheque';
      case 'deposito': return 'Depósito';
      default: return metodo;
    }
  };

  const iniciales = pago.usuario?.nombre
    ? pago.usuario.nombre.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '??';

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleBackdropClick}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div
        ref={modalRef}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
        style={{ animation: 'slideUp 0.25s ease-out' }}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <i className="ri-receipt-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Detalle del Pago</h2>
              <p className="text-teal-100 text-sm mt-0.5">ID: {pago.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
          {/* Monto y Estado */}
          <div className="flex items-center justify-between mb-6 pb-5 border-b border-gray-100">
            <div>
              <p className="text-sm text-gray-500 mb-1">Monto total</p>
              <p className="text-3xl font-bold text-gray-900">{formatearMonto(Number(pago.monto))}</p>
            </div>
            <div className="text-right flex flex-col items-end gap-2">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium capitalize ${obtenerColorEstado(pago.estado)}`}>
                <i className={`${obtenerIconoEstado(pago.estado)} text-base`}></i>
                {pago.estado}
              </span>
            </div>
          </div>

          {/* Info del Usuario */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Cliente</p>
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
              <div className="w-11 h-11 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-teal-700">{iniciales}</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{pago.usuario?.nombre || 'Usuario desconocido'}</p>
                <p className="text-xs text-gray-500 truncate">{pago.usuario?.email || '—'}</p>
                {pago.usuario?.telefono && (
                  <p className="text-xs text-gray-500">{pago.usuario.telefono}</p>
                )}
              </div>
              {pago.usuario?.tipo_membresia && (
                <span className="ml-auto px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full capitalize whitespace-nowrap">
                  {pago.usuario.tipo_membresia}
                </span>
              )}
            </div>
          </div>

          {/* Detalles del Pago */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Información del Pago</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Concepto</p>
                <p className="text-sm font-medium text-gray-900">{pago.concepto || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Método de Pago</p>
                <div className="flex items-center gap-1.5">
                  <i className={`${obtenerIconoMetodo(pago.metodo_pago)} text-gray-600`}></i>
                  <p className="text-sm font-medium text-gray-900">{obtenerNombreMetodo(pago.metodo_pago)}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Fecha de Pago</p>
                <p className="text-sm font-medium text-gray-900">{formatearFecha(pago.fecha_pago)}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Registrado</p>
                <p className="text-sm font-medium text-gray-900">{formatearFechaHora(pago.created_at)}</p>
              </div>
              {pago.banco && (
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <p className="text-xs text-gray-500 mb-1">Banco</p>
                  <p className="text-sm font-medium text-gray-900">{pago.banco}</p>
                </div>
              )}
              {pago.numero_comprobante && (
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <p className="text-xs text-gray-500 mb-1">N° Comprobante</p>
                  <p className="text-sm font-medium text-gray-900">{pago.numero_comprobante}</p>
                </div>
              )}
            </div>
          </div>

          {/* Notas */}
          {pago.notas && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Notas</p>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">{pago.notas}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
